import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import ExpertLayout from '../../components/ExpertLayout';
import { expertDashboardApi } from '../../lib/http';
import BookingStats from '../../components/expert/bookings/BookingStats';
import BookingFilters from '../../components/expert/bookings/BookingFilters';
import BookingList from '../../components/expert/bookings/BookingList';

// SVG icon component for refresh
const RefreshIcon = ({ className = '', spinning = false }: { className?: string; spinning?: boolean }) => (
  <svg
    className={`${className} ${spinning ? 'animate-spin' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

export type BookingFilter = 'all' | 'upcoming' | 'past' | 'cancelled';

const ExpertBookings: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<BookingFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<'date' | 'status' | 'client'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ['expert-profile'],
    queryFn: async () => {
      const response = await expertDashboardApi.profile.get();
      return response.data;
    },
  });

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['expert-bookings', activeFilter, searchTerm, statusFilter, sortBy, sortOrder],
    queryFn: async () => {
      const params: Record<string, string> = {
        filter: activeFilter,
        sort: sortBy,
        order: sortOrder,
      };

      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;

      const response = await expertDashboardApi.bookings.list(params);
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
  });

  const handleFilterChange = (filter: BookingFilter) => {
    setActiveFilter(filter);
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
  };

  const handleSort = (sort: 'date' | 'status' | 'client', order: 'asc' | 'desc') => {
    setSortBy(sort);
    setSortOrder(order);
  };

  const handleRefresh = async () => {
    if (isRefreshing || isFetching) return;

    setIsRefreshing(true);
    try {
      await refetch();
      toast.success('Bookings refreshed');
    } catch (error: any) {
      toast.error('Failed to refresh bookings');
    } finally {
      // Keep the spinning animation for a minimum time for better UX
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    }
  };

  const handleBookingAction = async (action: string, bookingId: string) => {
    try {
      switch (action) {
        case 'confirm':
          await expertDashboardApi.bookings.confirm(bookingId);
          toast.success('Booking confirmed successfully');
          break;
        case 'cancel':
          const reason = window.prompt('Cancellation reason (optional):');
          await expertDashboardApi.bookings.cancel(bookingId, reason || undefined);
          toast.success('Booking cancelled successfully');
          break;
        case 'complete':
          await expertDashboardApi.bookings.complete(bookingId);
          toast.success('Booking marked as completed');
          break;
        case 'no_show':
          await expertDashboardApi.bookings.markNoShow(bookingId);
          toast.success('Booking marked as no-show');
          break;
        case 'edit':
          // TODO: Implement edit modal
          toast('Edit functionality coming soon', { icon: 'ℹ️' });
          break;
        default:
          console.warn(`Unknown action: ${action}`);
          return;
      }
      await refetch();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Action failed';
      toast.error(errorMessage);
    }
  };

  return (
    <ExpertLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Bookings</h1>
            <p className="mt-2 text-sm text-gray-600">
              Manage and view all your appointments
            </p>
          </div>

          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || isFetching}
            className={`
              inline-flex items-center justify-center
              h-10 w-10 rounded-lg border
              transition-all duration-200 ease-out
              hover:scale-105 active:scale-95
              ${isRefreshing || isFetching
                ? 'border-purple-200 bg-purple-50 text-purple-600 cursor-not-allowed'
                : 'border-gray-200 bg-white text-gray-600 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-600'
              }
              focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
            `}
            title={isRefreshing || isFetching ? 'Refreshing...' : 'Refresh bookings'}
            aria-label={isRefreshing || isFetching ? 'Refreshing bookings' : 'Refresh bookings'}
          >
            <RefreshIcon
              className="h-5 w-5"
              spinning={isRefreshing || isFetching}
            />
          </button>
        </div>

        {/* Statistics */}
        {data?.statistics && (
          <div className="mb-8">
            <BookingStats statistics={data.statistics} currency={profile?.currency} />
          </div>
        )}

        {/* Filters */}
        <div className="mb-6">
          <BookingFilters
            activeFilter={activeFilter}
            onFilterChange={handleFilterChange}
            searchTerm={searchTerm}
            onSearch={handleSearch}
            statusFilter={statusFilter}
            onStatusFilter={handleStatusFilter}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-3">
                    <div className="h-5 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                  <div className="w-24 h-8 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">Error loading bookings</h3>
                <p className="mt-2 text-sm text-red-700">
                  {(error as any)?.message || 'Failed to load bookings. Please try again.'}
                </p>
                <button
                  onClick={() => refetch()}
                  className="mt-4 px-4 py-2 bg-red-100 text-red-800 rounded-md text-sm font-medium hover:bg-red-200"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bookings List */}
        {!isLoading && !isError && data?.bookings && (
          <BookingList
            bookings={data.bookings}
            onAction={handleBookingAction}
          />
        )}
      </div>
    </ExpertLayout>
  );
};

export default ExpertBookings;
